import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { supabase } from "./supabase";
import type { Session } from "@supabase/supabase-js";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export class SessionRequiredError extends Error {
  readonly status = 401;
}

let refreshPromise: ReturnType<typeof supabase.auth.refreshSession> | null = null;

/** Read the current session when sending, including binary uploads. Retry a rejected token once. */
export async function authenticatedFetch(url: string, init: RequestInit = {}, requireSession = true): Promise<Response> {
  const expired = () => new SessionRequiredError("Sua sessão expirou. Saia e entre novamente para continuar.");
  const { data, error } = await supabase.auth.getSession();
  if (error || (requireSession && !data.session?.access_token)) throw expired();

  const send = (token?: string) => {
    const headers = new Headers(init.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return fetch(url, { ...init, headers, credentials: "include" });
  };
  const response = await send(data.session?.access_token);
  if (response.status !== 401 || !data.session) return response;

  // Another request may already have renewed the token. Never replay as a different user.
  let current: { data: { session: Session | null }; error: unknown } = await supabase.auth.getSession();
  if (current.data.session?.user.id !== data.session.user.id) throw expired();
  if (current.data.session.access_token === data.session.access_token) {
    const pending = refreshPromise ??= supabase.auth.refreshSession();
    try { current = await pending; }
    finally { if (refreshPromise === pending) refreshPromise = null; }
  }
  if (current.error || !current.data.session?.access_token || current.data.session.user.id !== data.session.user.id) throw expired();
  return send(current.data.session.access_token);
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers: Record<string, string> = {};

  if (data) {
    headers["Content-Type"] = "application/json";
  }

  const res = await authenticatedFetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  }, false);

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    let res: Response;
    try { res = await authenticatedFetch(queryKey.join("/") as string, {}, false); }
    catch (error) {
      if (unauthorizedBehavior === "returnNull" && error instanceof SessionRequiredError) return null;
      throw error;
    }

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: true,
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
